import { useEffect, useRef, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useGlow } from '../hooks/useGlows'
import { Spinner } from '../components/ui/Spinner'
import { Badge } from '../components/Badge'
import { ErrorBanner } from '../components/ErrorBanner'
import { QrCodeDialog } from './glow/QrCodeDialog'
import { SettingsTab } from './glow/SettingsTab'
import { UsersTab } from './glow/UsersTab'
import { VersionsTab } from './glow/VersionsTab'
import { DataTab } from './glow/DataTab'
import { UsageTab } from './glow/UsageTab'
import { ApiKeysTab } from './glow/ApiKeysTab'
import { glowUrl, glowHost } from '../lib/glowUrl'

type Tab = 'settings' | 'users' | 'versions' | 'data' | 'usage' | 'api-keys'

const TABS: readonly Tab[] = [
  'settings',
  'users',
  'versions',
  'data',
  'usage',
  'api-keys',
]

// parseTab maps the ?tab= search param to a known Tab, defaulting to
// 'settings' for an absent or unrecognized value (F3 — a hand-edited / stale
// link never lands on a blank page).
function parseTab(raw: string | null): Tab {
  return TABS.includes(raw as Tab) ? (raw as Tab) : 'settings'
}

// A small QR-glyph — three finder rings plus a few data modules. Inline SVG
// (the console has no icon dependency; cf. VersionPicker's caret) so it tints
// with `currentColor` from the button's text color.
function QrIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 16 16"
      fill="currentColor"
      fillRule="evenodd"
      aria-hidden="true"
      className="shrink-0"
    >
      {/* finder rings (top-left, top-right, bottom-left) */}
      <path d="M1 1h5v5H1V1zm1 1v3h3V2H2z" />
      <path d="M10 1h5v5h-5V1zm1 1v3h3V2h-3z" />
      <path d="M1 10h5v5H1v-5zm1 1v3h3v-3H2z" />
      {/* finder centers */}
      <rect x="3" y="3" width="1" height="1" />
      <rect x="12" y="3" width="1" height="1" />
      <rect x="3" y="12" width="1" height="1" />
      {/* data modules */}
      <rect x="9" y="9" width="2" height="2" />
      <rect x="13" y="9" width="2" height="2" />
      <rect x="11" y="11" width="2" height="2" />
      <rect x="9" y="13" width="2" height="2" />
      <rect x="13" y="13" width="2" height="2" />
    </svg>
  )
}

export function GlowDetail() {
  const { id = '' } = useParams<{ id: string }>()
  const { data: glow, isLoading, error } = useGlow(id)
  // F3: the active tab lives in the URL (?tab=...) so it survives refresh, is
  // shareable/deep-linkable, and the browser back button moves between tabs.
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = parseTab(searchParams.get('tab'))
  // Keep the active tab visible when the strip scrolls horizontally on narrow
  // screens (D5/R3) — e.g. a deep-linked ?tab=api-keys lands with its tab in
  // view rather than clipped off the right edge.
  const activeTabRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    activeTabRef.current?.scrollIntoView({ inline: 'nearest', block: 'nearest' })
  }, [tab])
  // The QR-code modal mounts on open so its query fires only when opened (no
  // eager fetch on every glow-detail view).
  const [qrOpen, setQrOpen] = useState(false)
  const setTab = (next: Tab) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.set('tab', next)
        return params
      },
      // Default (push) so back navigates between tabs rather than off the page.
    )
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }
  if (error || !glow) {
    return (
      <ErrorBanner>
        {error ? (error as Error).message : 'Glow not found.'}{' '}
        <Link to="/glows" className="font-medium text-glow-600 hover:underline">
          Back to glows
        </Link>
      </ErrorBanner>
    )
  }

  const tabClass = (active: boolean) =>
    `min-h-[44px] whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium ${
      active
        ? 'border-glow-500 text-cream'
        : 'border-transparent text-muted hover:text-cream'
    }`

  return (
    <div>
      <div className="mb-6">
        <Link
          to="/glows"
          className="text-xs font-medium text-muted hover:underline"
        >
          ← All glows
        </Link>
        <h1 className="mt-2 text-2xl font-semibold">{glow.title || glow.name}</h1>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted">
          <a
            href={glowUrl(glow.name)}
            target="_blank"
            rel="noreferrer"
            className="break-all hover:underline"
          >
            {glowHost(glow.name)}
          </a>
          <button
            type="button"
            onClick={() => setQrOpen(true)}
            className="inline-flex min-h-[44px] items-center gap-1 text-xs font-medium text-glow-600 hover:underline"
            aria-label="Show QR code"
          >
            <QrIcon />
            QR code
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          <Badge tone={glow.status === 'Lit' ? 'amber' : 'slate'}>
            {glow.status}
          </Badge>
          {glow.status === 'Dark' && glow.dark_reason === 'downgrade' && (
            <Badge tone="red">Darkened — over limit</Badge>
          )}
          <Badge
            tone={
              glow.auth_mode === 'Public'
                ? 'blue'
                : glow.auth_mode === 'Accounts'
                  ? 'green'
                  : 'slate'
            }
          >
            {glow.auth_mode}
          </Badge>
          <Badge tone="slate">{glow.region}</Badge>
        </div>
      </div>

      <div className="mb-4 border-b border-line">
        <nav className="flex gap-1 overflow-x-auto -mx-4 px-4" aria-label="Glow sections">
          <button
            ref={tab === 'settings' ? activeTabRef : undefined}
            className={tabClass(tab === 'settings')}
            onClick={() => setTab('settings')}
          >
            Settings
          </button>
          <button
            ref={tab === 'users' ? activeTabRef : undefined}
            className={tabClass(tab === 'users')}
            onClick={() => setTab('users')}
          >
            Users
          </button>
          <button
            ref={tab === 'versions' ? activeTabRef : undefined}
            className={tabClass(tab === 'versions')}
            onClick={() => setTab('versions')}
          >
            Versions
          </button>
          <button
            ref={tab === 'data' ? activeTabRef : undefined}
            className={tabClass(tab === 'data')}
            onClick={() => setTab('data')}
          >
            Data
          </button>
          <button
            ref={tab === 'usage' ? activeTabRef : undefined}
            className={tabClass(tab === 'usage')}
            onClick={() => setTab('usage')}
          >
            Usage
          </button>
          <button
            ref={tab === 'api-keys' ? activeTabRef : undefined}
            className={tabClass(tab === 'api-keys')}
            onClick={() => setTab('api-keys')}
          >
            API keys
          </button>
        </nav>
      </div>

      {tab === 'settings' && <SettingsTab glow={glow} />}
      {tab === 'users' && <UsersTab glow={glow} />}
      {tab === 'versions' && <VersionsTab glow={glow} />}
      {tab === 'data' && <DataTab glow={glow} />}
      {tab === 'usage' && <UsageTab glow={glow} />}
      {tab === 'api-keys' && <ApiKeysTab glow={glow} />}

      {qrOpen && <QrCodeDialog glow={glow} onClose={() => setQrOpen(false)} />}
    </div>
  )
}

export default GlowDetail
